import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';

import AuthContext from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import useStatisticJob from '../../hooks/useStatisticJob';
import StatisticViewShell from '../ui/StatisticViewShell';
import { STATISTIC_TYPE_COPAGO_COMPLIANCE } from '../../constants/statisticTypes';

const formatEuroCents = cents => {
  const n = Number(cents || 0) / 100;
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const formatPct = value => {
  if (value == null) return '–';
  return (
    Number(value).toLocaleString('de-DE', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) + ' %'
  );
};

const formatNumber = value => Number(value || 0).toLocaleString('de-DE');

const diffColor = cents => {
  const n = Number(cents || 0);
  if (n < 0) return 'error.main';
  if (n > 0) return 'warning.main';
  return 'success.main';
};

// Severity semantics: red = marked as paid in the tool but the register shows
// less (possible employee fraud); yellow = cashed at the register without a
// tool booking (process skipped); neutral = upgrade-only walk-in sale (fine).
const SEVERITY_CONFIG = {
  fraudSuspect: {
    label: 'Bezahlt markiert, nicht abkassiert',
    chipColor: 'error',
    tooltip:
      'Im Tool als offline bezahlt markiert, aber an der Kasse fehlt (ein Teil des) Umsatz(es) — möglicher Betrugsfall, bitte prüfen.',
  },
  processViolation: {
    label: 'Abkassiert ohne Buchung',
    chipColor: 'warning',
    tooltip:
      'An der Kasse verkauft, ohne dass eine entsprechende Buchung im Tool vorliegt — Prozess wurde nicht eingehalten.',
  },
  upgradeDirectSale: {
    label: 'Kassen-Direktverkauf',
    chipColor: 'default',
    tooltip:
      'Upgrade-Artikel an der Kasse verkauft ohne Buchung — unkritisch, wird nur zur Info angezeigt.',
  },
};

const SeverityChip = ({ severity }) => {
  const config = SEVERITY_CONFIG[severity];
  if (!config) return <Typography variant='caption' color='text.secondary'>—</Typography>;
  return (
    <Tooltip title={config.tooltip}>
      <Chip label={config.label} size='small' color={config.chipColor} variant='outlined' />
    </Tooltip>
  );
};

const severityRowSx = severity => theme => {
  if (severity === 'fraudSuspect') {
    return { backgroundColor: alpha(theme.palette.error.main, 0.08) };
  }
  if (severity === 'processViolation') {
    return { backgroundColor: alpha(theme.palette.warning.main, 0.08) };
  }
  return {};
};

const KPI = ({ label, value, hint, valueColor }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center', height: '100%' }}
  >
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='h5' sx={{ mt: 0.5, fontWeight: 600, color: valueColor || 'inherit' }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant='caption' color='text.secondary'>
        {hint}
      </Typography>
    )}
  </Paper>
);

const SectionHeader = ({ title }) => (
  <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1, mt: 3 }}>
    <Typography variant='h6'>{title}</Typography>
    <Divider sx={{ flex: 1 }} />
  </Stack>
);

const EmptyRow = ({ colSpan, text }) => (
  <TableRow>
    <TableCell colSpan={colSpan}>
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant='body2' color='text.secondary'>
          {text || 'Keine Daten im gewählten Zeitraum.'}
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

const CopagoComplianceStatistics = () => {
  const authCtx = useContext(AuthContext);
  const { getBranches } = useBranch();

  const canView = authCtx.checkRight('statistic:read');

  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchesReady, setBranchesReady] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('day').format('YYYY-MM-DD'),
    branchId: undefined,
  });
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const getBranchesRef = useRef(getBranches);
  useEffect(() => {
    getBranchesRef.current = getBranches;
  }, [getBranches]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!canView) return;
      try {
        const list = await getBranchesRef.current();
        if (cancelled) return;
        setBranchOptions([
          { _id: '', label: 'Alle Standorte' },
          ...(list || []).map(b => ({
            _id: typeof b._id === 'string' ? b._id : String(b._id),
            label: b.locationName || b.longName || 'Standort',
          })),
        ]);
      } catch (_) {
        if (!cancelled) setBranchOptions([{ _id: '', label: 'Alle Standorte' }]);
      }
      if (!cancelled) setBranchesReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const filterParams = useMemo(() => {
    if (!canView || !branchesReady) return null;
    const p = {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
    };
    if (appliedFilter.branchId) p.branchId = appliedFilter.branchId;
    return p;
  }, [canView, branchesReady, appliedFilter]);

  const {
    payload,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    refresh,
    loadCacheEntry,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_COPAGO_COMPLIANCE,
    endpoint: '/api/v1/copagoArticle/complianceStatistics',
    filterParams,
    autoFetchOnMount: true,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_COPAGO_COMPLIANCE,
  });

  const handleApply = useCallback(
    (nextStart, nextEnd) => {
      const s = nextStart || startDate;
      const e = nextEnd || endDate;
      if (!s || !e || e.isBefore(s, 'day')) return;
      setAppliedFilter({
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
        branchId: selectedBranchId || undefined,
      });
      setSelectedEntryId(null);
    },
    [startDate, endDate, selectedBranchId]
  );

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId || null);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const startDatePickerProps = useMemo(() => ({ maxDate: endDate }), [endDate]);
  const endDatePickerProps = useMemo(() => ({ minDate: startDate }), [startDate]);
  const applyIsDisabled = !startDate || !endDate || endDate.isBefore(startDate, 'day');
  const refreshIsDisabled = status === 'fetching' || status === 'pending' || status === 'running';
  const shellFilterParamConfig = useMemo(() => ({ branchScope: { hidden: true } }), []);

  if (!canView) {
    return <Navigate to='/welcome' replace />;
  }

  const totals = payload?.totals;

  return (
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      startDatePickerProps={startDatePickerProps}
      endDatePickerProps={endDatePickerProps}
      branchOptions={branchOptions}
      selectedBranchId={selectedBranchId}
      onBranchChange={setSelectedBranchId}
      branchSelectIdPrefix='copago-compliance-branch'
      onApply={handleApply}
      applyDisabled={applyIsDisabled}
      applyLabel='Auswerten'
      applyInFilterRow
      status={status}
      isStale={isStale}
      cacheComputedAt={cacheComputedAt}
      cacheExpiresAt={cacheExpiresAt}
      job={job}
      error={error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedEntryId}
      onSelectEntry={handleSelectEntry}
      entriesLoading={entriesLoading}
      onRefresh={refresh}
      refreshDisabled={refreshIsDisabled}
      filterParamConfig={shellFilterParamConfig}
      hasPayload={payload != null}
      externallyLoading={!branchesReady}
    >
      {payload && totals && (
        <Fragment>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Vergleicht die im Tool als offline bezahlt markierten Verkäufe (nach Zahlzeitpunkt)
            mit den Bon-Daten der Kasse im gleichen Zeitraum. Bon-Artikel ohne Matching (können im
            Tool nicht abgebildet werden) sind aus dem Vergleich ausgeschlossen. Rot = im Tool als
            bezahlt markiert, aber an der Kasse nicht abkassiert (möglicher Betrug). Gelb = an der
            Kasse abkassiert ohne Buchung im Tool (Prozess nicht eingehalten). Kassen-Direktverkäufe
            von Upgrades ohne Buchung sind unkritisch und werden nur zur Info gezeigt.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Bezahlt markiert, nicht abkassiert'
                value={formatEuroCents(totals.fraudSuspectRevenueCents)}
                valueColor={totals.fraudSuspectRevenueCents > 0 ? 'error.main' : 'success.main'}
                hint='möglicher Betrug — bitte prüfen'
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Abkassiert ohne Buchung'
                value={formatEuroCents(totals.processViolationRevenueCents)}
                valueColor={
                  totals.processViolationRevenueCents > 0 ? 'warning.main' : 'success.main'
                }
                hint='Prozess nicht eingehalten (Tickets ohne Buchung)'
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Upgrade-Direktverkäufe Kasse'
                value={formatEuroCents(totals.upgradeDirectSaleRevenueCents)}
                hint='ohne Buchung verkauft — unkritisch'
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Offline gebucht, noch nicht kassiert'
                value={formatEuroCents(totals.toolUnpaidOfflineRevenueCents)}
                hint='offene Offline-Zahlungen im Zeitraum'
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Tool-Umsatz (offline, bezahlt markiert)'
                value={formatEuroCents(totals.toolMatchedRevenueCents)}
                hint={`${formatNumber(totals.bookingsConsidered)} Buchungen betrachtet`}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Bon-Umsatz (gematchte Artikel)'
                value={formatEuroCents(totals.bonMatchedRevenueCents)}
                hint={`${formatNumber(totals.bonsConsidered)} Bons betrachtet`}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <Typography variant='caption' color='text.secondary'>
                  Differenz (Tool − Bon)
                </Typography>
                <Typography
                  variant='h5'
                  sx={{ mt: 0.5, fontWeight: 600, color: diffColor(totals.diffCents) }}
                >
                  {formatEuroCents(totals.diffCents)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Abdeckung Tool / Bon'
                value={formatPct(totals.coveragePercent)}
                hint='100 % = alles Abkassierte wurde im Tool gebucht'
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Bon-Umsatz ausgeschlossen'
                value={formatEuroCents(totals.bonExcludedRevenueCents)}
                hint='Artikel ohne Matching (z.B. Gastro)'
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <KPI
                label='Tool-Umsatz ohne Matching'
                value={formatEuroCents(totals.toolUnmatchableRevenueCents)}
                hint='Offline bezahlte Posten ohne Copago-Artikel'
              />
            </Grid>
          </Grid>

          <SectionHeader title='Vergleich pro Copago-Artikel' />
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Copago-Artikel</TableCell>
                  <TableCell>Bewertung</TableCell>
                  <TableCell align='right'>Tool bezahlt</TableCell>
                  <TableCell align='right'>Tool-Anzahl</TableCell>
                  <TableCell align='right'>Bon-Umsatz</TableCell>
                  <TableCell align='right'>Bon-Menge</TableCell>
                  <TableCell align='right'>Differenz</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(payload.byArticle || []).map(row => (
                  <TableRow key={row.articleId} hover sx={severityRowSx(row.severity)}>
                    <TableCell>
                      {row.copagoId != null ? `${row.copagoId} – ` : ''}
                      {row.articleName}
                      {row.toolUnpaidRevenueCents > 0 ? (
                        <Typography variant='caption' color='text.secondary' display='block'>
                          zzgl. {formatEuroCents(row.toolUnpaidRevenueCents)} offen (nicht kassiert)
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <SeverityChip severity={row.severity} />
                    </TableCell>
                    <TableCell align='right'>{formatEuroCents(row.toolRevenueCents)}</TableCell>
                    <TableCell align='right'>{formatNumber(row.toolUnits)}</TableCell>
                    <TableCell align='right'>{formatEuroCents(row.bonRevenueCents)}</TableCell>
                    <TableCell align='right'>{formatNumber(row.bonQuantity)}</TableCell>
                    <TableCell
                      align='right'
                      sx={{
                        color:
                          row.severity === 'upgradeDirectSale'
                            ? 'text.secondary'
                            : diffColor(row.diffCents),
                        fontWeight: 600,
                      }}
                    >
                      {formatEuroCents(row.diffCents)}
                    </TableCell>
                  </TableRow>
                ))}
                {(payload.byArticle || []).length === 0 && <EmptyRow colSpan={7} />}
              </TableBody>
            </Table>
          </TableContainer>

          <SectionHeader title='Verlauf pro Tag' />
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Datum</TableCell>
                  <TableCell align='right'>Tool-Umsatz (offline)</TableCell>
                  <TableCell align='right'>Bon-Umsatz (gematcht)</TableCell>
                  <TableCell align='right'>Differenz</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(payload.byDay || []).map(row => {
                  const diff = (row.toolRevenueCents || 0) - (row.bonRevenueCents || 0);
                  return (
                    <TableRow key={row.date} hover>
                      <TableCell>{dayjs(row.date).format('DD.MM.YYYY')}</TableCell>
                      <TableCell align='right'>{formatEuroCents(row.toolRevenueCents)}</TableCell>
                      <TableCell align='right'>{formatEuroCents(row.bonRevenueCents)}</TableCell>
                      <TableCell align='right' sx={{ color: diffColor(diff), fontWeight: 600 }}>
                        {formatEuroCents(diff)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(payload.byDay || []).length === 0 && <EmptyRow colSpan={4} />}
              </TableBody>
            </Table>
          </TableContainer>

          <SectionHeader title='Ausgeschlossene Bon-Artikel (Top, ohne Matching)' />
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Diese Artikel sind keinem Upgrade / keiner Preisliste zugeordnet und fließen deshalb
            nicht in den Vergleich ein.
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Copago-Artikel</TableCell>
                  <TableCell align='right'>Bon-Umsatz</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(payload.excludedTopArticles || []).map(row => (
                  <TableRow key={row.copagoId} hover>
                    <TableCell>
                      {row.copagoId} – {row.name || 'Unbekannter Artikel'}
                    </TableCell>
                    <TableCell align='right'>{formatEuroCents(row.revenueCents)}</TableCell>
                  </TableRow>
                ))}
                {(payload.excludedTopArticles || []).length === 0 && (
                  <EmptyRow colSpan={2} text='Keine ausgeschlossenen Artikel im Zeitraum.' />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Fragment>
      )}
    </StatisticViewShell>
  );
};

export default CopagoComplianceStatistics;
